import { observable } from "@legendapp/state";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import { isSnapshotOrigin } from "../board-snapshot/snapshot-origins";
import type { Board } from "./boards.types";

export const boards$ = observable(
  syncedCrudPersist<Board>({
    persist: {
      name: "boards",
    },
    waitFor: appDataReady$,
    list: async () => {
      return await trpc.boards.list.query();
    },
    update: async ({ id, ...input }) => {
      if (!id) return;
      if (isSnapshotOrigin(id)) return;

      return await trpc.boards.update.mutate({
        id,
        ...input,
      });
    },
    create: async (input) => {
      if (isSnapshotOrigin(input.id)) return input;
      return await trpc.boards.create.mutate(input);
    },
    delete: async (input) => {
      if (!input.id) return;
      if (isSnapshotOrigin(input.id)) return;

      await trpc.boards.delete.mutate({
        boardId: input.id,
      });
    },
    subscribe: ({ update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];
        const filtered = items.filter((item) => !isSnapshotOrigin(item?.id));
        if (filtered.length === 0) return;
        update({
          mode: "merge",
          value: filtered,
        });
      };

      channel.subscribe("boards", handler);

      return () => {
        channel.unsubscribe("boards", handler);
      };
    },
  }),
);
